import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { User, LogOut, ChevronDown, Lock, Users } from "lucide-react";
import { authService, employeeService } from "../../services/api";
import AdminCheckInOut from "./AdminCheckInOut";
import NavbarNotifications from "./NavbarNotifications";
import NavbarFlashMarquee from "./NavbarFlashMarquee";
import { getAuthJson } from "../../utils/authStorage";

function Navbar({
  darkMode,
  handleLogout,
  isEmployeeView,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [user, setUser] = useState(() => getAuthJson("currentUser", null));
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const [companyData, setCompanyData] = useState(() => getAuthJson("companyData", null));

  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordMessage, setChangePasswordMessage] = useState("");
  const [showEssModal, setShowEssModal] = useState(false);
  const [essEmployees, setEssEmployees] = useState([]);
  const [essLoading, setEssLoading] = useState(false);
  const [essError, setEssError] = useState("");
  const [essSearch, setEssSearch] = useState("");
  const [proxyLoginLoadingId, setProxyLoginLoadingId] = useState(null);

  useEffect(() => {
      const user = getAuthJson("currentUser", null);
      setUser(user);

      // Try to get company data from proxy-aware auth storage
      let companyData = null;
      try {
        companyData = getAuthJson("companyData", null);
      } catch (error) {
        console.error("Error parsing company data:", error);
      }

      setCompanyData(companyData);

      // Set browser tab title immediately
      if (companyData?.name) {
        document.title = companyData.name;
      }

      function handleClickOutside(event) {
        // Check if click is outside the dropdown button and the portal dropdown
        const isOutsideButton =
          dropdownRef.current && !dropdownRef.current.contains(event.target);
        const isOutsidePortal = !event.target.closest("[data-portal-dropdown]");

        if (isOutsideButton && isOutsidePortal) {
          setIsDropdownOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    // Listen for changes in storage to update company data and tab title
    useEffect(() => {
      const handleStorageChange = () => {
        try {
          const newCompanyData = getAuthJson("companyData", null);
          if (newCompanyData) {
            setCompanyData(newCompanyData);
            // Update browser tab title when company data changes
            if (newCompanyData?.name) {
              document.title = newCompanyData.name;
            }
          }
          const nextUser = getAuthJson("currentUser", null);
          if (nextUser) setUser(nextUser);
        } catch (error) {
          console.error("Error parsing updated company data:", error);
        }
      };

      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    }, []);

    const toggleDropdown = () => {
      if (!isDropdownOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        // `fixed` uses viewport coords — do not add window.scrollY
        setDropdownPosition({
          top: rect.bottom + 4,
          right: Math.max(8, window.innerWidth - rect.right),
        });
      }
      setIsDropdownOpen(!isDropdownOpen);
    };

    const handleChangePassword = async (e) => {
      e.preventDefault();
      setChangePasswordMessage("");

      // Validate passwords match
      if (
        changePasswordData.newPassword !== changePasswordData.confirmPassword
      ) {
        setChangePasswordMessage("New passwords do not match");
        return;
      }

      // Validate password length
      if (changePasswordData.newPassword.length < 6) {
        setChangePasswordMessage(
          "New password must be at least 6 characters long"
        );
        return;
      }

      setChangePasswordLoading(true);

      try {
        const response = await authService.changePassword(
          changePasswordData.currentPassword,
          changePasswordData.newPassword
        );

        if (response.success) {
          setChangePasswordMessage("Password changed successfully!");
          setChangePasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });

          // Close modal after 2 seconds
          setTimeout(() => {
            setShowChangePassword(false);
            setChangePasswordMessage("");
          }, 2000);
        } else {
          setChangePasswordMessage(
            response.message || "Failed to change password"
          );
        }
      } catch (error) {
        setChangePasswordMessage(error.message || "An error occurred");
      } finally {
        setChangePasswordLoading(false);
      }
    };

    const resetChangePasswordForm = () => {
      setChangePasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setChangePasswordMessage("");
      setChangePasswordLoading(false);
    };

    const extractEmployeesFromResponse = (response) => {
      if (Array.isArray(response?.employees)) return response.employees;
      if (Array.isArray(response?.data?.employees)) return response.data.employees;
      if (Array.isArray(response?.data?.rows)) return response.data.rows;
      if (Array.isArray(response?.rows)) return response.rows;
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response)) return response;
      return [];
    };

    const readEmployeesPagination = (response) => {
      const p = response?.pagination || response?.meta || response?.data?.pagination || {};
      const totalCount = Number(
        p.totalCount ??
          p.total ??
          response?.totalCount ??
          response?.total ??
          response?.data?.totalCount ??
          response?.data?.total ??
          0
      );
      const totalPages = Number(
        p.totalPages ??
          response?.totalPages ??
          response?.data?.totalPages ??
          0
      );
      const limit = Number(p.limit ?? 0);
      return {
        totalCount: Number.isFinite(totalCount) ? totalCount : 0,
        totalPages: Number.isFinite(totalPages) ? totalPages : 0,
        limit: Number.isFinite(limit) ? limit : 0,
      };
    };

    const getEmployeeEmail = (emp) => {
      const primary = emp?.email;
      if (typeof primary === "string" && primary.trim()) return primary.trim();
      if (primary && typeof primary === "object" && typeof primary.name === "string") {
        return primary.name.trim();
      }
      const fallbacks = [
        emp?.officialEmail,
        emp?.user?.email,
        emp?.personalEmail,
      ];
      return (fallbacks.find((value) => typeof value === "string" && value.trim()) || "").trim();
    };

    const buildEssEmployee = (emp) => {
      const fullName =
        `${emp?.user?.name || emp?.user?.firstName || emp?.firstName || ""} ${
          emp?.user?.lastName || emp?.lastName || ""
        }`.trim();
      const fallbackName =
        emp?.name ||
        fullName ||
        emp?.officialEmail ||
        emp?.email ||
        emp?.user?.email ||
        `Employee #${emp?.id ?? emp?.empCode ?? "?"}`;
      const id = emp?.id ?? emp?.employeeId ?? null;
      return {
        id,
        userId: emp?.user?.id || emp?.userId || null,
        name: fallbackName,
        code: emp?.empCode || emp?.employeeCode || emp?.employeeId || emp?.emp_code || "",
        email: getEmployeeEmail(emp),
      };
    };

    const normalizeEssEmployees = (rows = []) => {
      const deduped = new Map();
      rows.forEach((emp) => {
        const built = buildEssEmployee(emp);
        const key = built.id ?? `code:${String(built.code || "").toLowerCase()}`;
        if (key == null || key === "code:") return;
        if (built.id == null) return; // proxy login needs numeric employee id
        deduped.set(built.id, built);
      });
      return Array.from(deduped.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      );
    };

    const openEssModal = async () => {
      setIsDropdownOpen(false);
      setShowEssModal(true);
      setEssError("");
      setEssSearch("");
      // Never trust the old grid-page cache (often only 10 rows).
      try {
        localStorage.removeItem("essEmployeeListCache");
      } catch {
        /* ignore */
      }
      setEssEmployees([]);
      setEssLoading(true);

      try {
        const pageLimit = 1000;
        let currentPage = 1;
        let maxPages = 1;
        const allRows = [];
        const safetyCap = 100;

        while (currentPage <= maxPages && currentPage <= safetyCap) {
          const response = await employeeService.getEmployees(
            {
              page: currentPage,
              limit: pageLimit,
              excludeEmploymentTypes: "External,external",
            },
            // Access ESS is an admin proxy tool — load the full company roster,
            // not only the caller's data-scope slice (which can look like "first page").
            { skipDataScope: true }
          );

          const pageRows = extractEmployeesFromResponse(response);
          allRows.push(...pageRows);

          const { totalCount, totalPages } = readEmployeesPagination(response);
          if (totalPages > 0) {
            maxPages = totalPages;
          } else if (totalCount > 0) {
            maxPages = Math.max(1, Math.ceil(totalCount / pageLimit));
          } else if (pageRows.length < pageLimit) {
            maxPages = currentPage;
          } else {
            // Unknown total — keep going until a short page.
            maxPages = currentPage + 1;
          }

          // Stop early if we already collected everything.
          if (totalCount > 0 && allRows.length >= totalCount) {
            break;
          }
          if (pageRows.length === 0) {
            break;
          }

          currentPage += 1;
        }

        const normalized = normalizeEssEmployees(allRows);
        if (!normalized.length) {
          setEssError("No employees found for Access ESS.");
        }
        setEssEmployees(normalized);
        try {
          localStorage.setItem(
            "essEmployeeListCache",
            JSON.stringify(normalized)
          );
        } catch {
          /* ignore quota */
        }
      } catch (error) {
        setEssEmployees([]);
        setEssError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load employees"
        );
      } finally {
        setEssLoading(false);
      }
    };

    const handleEssProxyLogin = async (employee) => {
      setEssError("");
      setProxyLoginLoadingId(employee.id);
      try {
        // Get a 2-minute one-time proxy token from the backend.
        const proxyToken = await authService.issueEssProxyToken(employee.id);

        // Open the ESS session in a NEW WINDOW with its own isolated sessionStorage.
        // noopener ensures the new window cannot access this admin window,
        // and its sessionStorage starts empty (no admin data leaks in).
        const url = `${window.location.origin}/ess-proxy?t=${encodeURIComponent(proxyToken)}`;
        const newWin = window.open(
          url,
          "_blank",
          "noopener,noreferrer,width=1440,height=900"
        );

        if (!newWin) {
          setEssError(
            "Popup was blocked by your browser. Please allow popups for this site and try again."
          );
        } else {
          setShowEssModal(false);
          setEssSearch("");
        }
      } catch (error) {
        const msg =
          typeof error === "string"
            ? error
            : error?.response?.data?.message ||
              error?.message ||
              error?.error ||
              "Failed to open ESS session. Please try again.";
        setEssError(msg);
      } finally {
        setProxyLoginLoadingId(null);
      }
    };

    const filteredEssEmployees = essEmployees.filter((employee) => {
      const search = essSearch.trim().toLowerCase();
      if (!search) return true;
      return (
        employee.name.toLowerCase().includes(search) ||
        String(employee.code || "").toLowerCase().includes(search) ||
        String(employee.email || "").toLowerCase().includes(search)
      );
    });

    return (
      // Keep navbar BELOW app modals (see --ds-z-nav). Never raise above --ds-z-modal.
      <nav
        className={`sticky top-0 z-[var(--ds-z-nav)] w-full overflow-hidden border-b border-[hsl(var(--ds-border))] font-sans antialiased ${
          darkMode ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        }`}
      >
        <div className="relative flex h-16 w-full items-center justify-between overflow-hidden sm:h-20">
          {/* Company name — constrained so marquee / right cluster never overflow */}
          <div className="relative z-10 flex h-full max-w-[min(12rem,22vw)] shrink-0 items-center pl-2 sm:max-w-[min(16rem,24vw)] sm:pl-2.5">
            <h2 className="truncate font-sans text-base font-semibold leading-6 tracking-tight text-[hsl(var(--ds-fg))] sm:text-xl sm:leading-7 dark:text-[hsl(var(--ds-fg))]">
              {companyData?.name || "Company"}
            </h2>
          </div>

          <NavbarFlashMarquee darkMode={darkMode} />

          {/* spacer so right cluster doesn't collide with long names */}
          <div className="pointer-events-none min-w-0 flex-1" aria-hidden />

          <div className="relative z-10 flex shrink-0 items-center gap-3 pr-3 sm:pr-6 lg:pr-8">
              <div className="mr-1 flex shrink-0 items-center pr-1">
                <AdminCheckInOut darkMode={darkMode} variant="navbar" />
              </div>
              <NavbarNotifications darkMode={darkMode} />
              <div className="flex items-center relative" ref={dropdownRef}>
                <button
                  ref={buttonRef}
                  onClick={toggleDropdown}
                  className="flex items-center focus:outline-none"
                  aria-label="User menu"
                  aria-haspopup="true"
                >
                  <span
                    className={`mr-2 hidden text-sm sm:inline ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
                    }`}
                  >
                    {user?.name ||
                      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
                      user?.email ||
                      "User"}
                  </span>
                  <User
                    className={`h-8 w-8 rounded-full p-1 ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-accent-fg))]"
                        : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                    }`}
                  />
                  <ChevronDown
                    className={`ml-1 h-4 w-4 ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-[hsl(var(--ds-fg))]/50 overflow-y-auto h-full w-full z-[12200]">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-[hsl(var(--ds-surface))] dark:bg-[hsl(var(--ds-surface))]">
              <div className="mt-3">
                <h3 className="mb-4 text-center text-lg font-medium text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-fg))]">
                  Change Password
                </h3>

                {changePasswordMessage && (
                  <div
                    className={`mb-4 p-3 rounded text-sm ${
                      changePasswordMessage.includes("successfully")
                        ? "bg-green-100 text-green-700 border border-green-400"
                        : "bg-red-100 text-red-700 border border-red-400"
                    }`}
                  >
                    {changePasswordMessage}
                  </div>
                )}

                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      required
                      value={changePasswordData.currentPassword}
                      onChange={(e) =>
                        setChangePasswordData({
                          ...changePasswordData,
                          currentPassword: e.target.value,
                        })
                      }
                      className="rh-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      required
                      value={changePasswordData.newPassword}
                      onChange={(e) =>
                        setChangePasswordData({
                          ...changePasswordData,
                          newPassword: e.target.value,
                        })
                      }
                      className="rh-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      required
                      value={changePasswordData.confirmPassword}
                      onChange={(e) =>
                        setChangePasswordData({
                          ...changePasswordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="rh-input"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowChangePassword(false);
                        resetChangePasswordForm();
                      }}
                      className="flex-1 rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:border-[#3C3C4C] dark:bg-[#2a2a2a] dark:text-gray-200 dark:hover:bg-[#333]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={changePasswordLoading}
                      className={`rh-btn-primary flex-1 px-4 py-2 ${
                        changePasswordLoading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {changePasswordLoading
                        ? "Changing..."
                        : "Change Password"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showEssModal && createPortal(
          <div className="fixed inset-0 z-[12200] flex items-center justify-center bg-gray-600/50 p-4">
            <div
              className={`w-full max-w-2xl rounded-md border shadow-lg ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <div className="border-b border-[hsl(var(--ds-border))] px-4 py-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Access ESS
                </h3>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Select an employee to open their ESS page without password.
                  {!essLoading && essEmployees.length > 0
                    ? ` (${essEmployees.length} employee${
                        essEmployees.length === 1 ? "" : "s"
                      })`
                    : ""}
                </p>
              </div>
              <div className="space-y-3 p-4">
                <input
                  type="text"
                  value={essSearch}
                  onChange={(e) => setEssSearch(e.target.value)}
                  placeholder="Search by name, code, or email"
                  className="rh-input"
                />

                {essError && (
                  <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {essError}
                  </div>
                )}

                <div className="max-h-80 space-y-2 overflow-y-auto">
                  {essLoading ? (
                    <div className="text-sm text-gray-600 dark:text-gray-400">Loading employees...</div>
                  ) : filteredEssEmployees.length === 0 ? (
                    <div className="text-sm text-gray-600 dark:text-gray-400">No employees found.</div>
                  ) : (
                    filteredEssEmployees.map((employee) => (
                      <button
                        key={employee.id}
                        type="button"
                        onClick={() => handleEssProxyLogin(employee)}
                        disabled={proxyLoginLoadingId === employee.id}
                        className={`flex w-full items-center justify-between rounded-md border border-l-[3px] px-3 py-2 text-left transition ${
                          darkMode
                            ? "border-[hsl(var(--ds-border))] border-l-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                            : "border-[hsl(var(--ds-border))] border-l-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                        } ${proxyLoginLoadingId === employee.id ? "opacity-60" : ""}`}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-[hsl(var(--ds-fg))]">
                            {employee.name}
                          </div>
                          <div className="truncate text-xs text-[hsl(var(--ds-muted-fg))]">
                            {employee.code ? `${employee.code} • ` : ""}
                            {employee.email || "No email"}
                          </div>
                        </div>
                        <span className="text-xs font-medium text-[hsl(var(--ds-primary))]">
                          {proxyLoginLoadingId === employee.id ? "Opening..." : "Access"}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end border-t border-[hsl(var(--ds-border))] px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEssModal(false);
                    setEssSearch("");
                    setEssError("");
                  }}
                  className="rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-2 text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-accent))]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        , document.body)}

        {/* Portal Dropdown */}
        {isDropdownOpen &&
          createPortal(
            <div
              data-portal-dropdown
              className={`fixed z-[var(--ds-z-dropdown)] w-48 rounded-xl border py-1 shadow-lg ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
              style={{
                top: `${dropdownPosition.top}px`,
                right: `${dropdownPosition.right}px`,
              }}
            >
              <button
                onClick={() => {
                  setShowChangePassword(true);
                  setIsDropdownOpen(false);
                }}
                className={`flex w-full items-center px-4 py-2 text-sm focus:outline-none focus-visible:bg-[hsl(var(--ds-muted))] ${
                  darkMode
                    ? "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-accent-fg))]"
                    : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-secondary))]"
                }`}
              >
                <Lock className="mr-2 h-4 w-4" />
                Change Password
              </button>
              <button
                onClick={() => {
                  console.log(
                    "Logout button clicked, handleLogout:",
                    typeof handleLogout
                  );
                  if (typeof handleLogout === "function") {
                    handleLogout();
                    setIsDropdownOpen(false);
                  } else {
                    console.error(
                      "handleLogout is not a function:",
                      handleLogout
                    );
                    // Fallback: clear localStorage and reload page
                    localStorage.clear();
                    window.location.href = "/login";
                  }
                }}
                className={`flex w-full items-center px-4 py-2 text-sm focus:outline-none focus-visible:bg-[hsl(var(--ds-muted))] ${
                  darkMode
                    ? "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-accent-fg))]"
                    : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-secondary))]"
                }`}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </button>
              {!isEmployeeView && (
                <button
                  onClick={openEssModal}
                  className={`flex w-full items-center px-4 py-2 text-sm focus:outline-none focus-visible:bg-[hsl(var(--ds-muted))] ${
                    darkMode
                      ? "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-accent-fg))]"
                      : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-secondary))]"
                  }`}
                >
                  <Users className="mr-2 h-4 w-4" />
                  Access ESS
                </button>
              )}
            </div>,
            document.body
          )}
      </nav>
    );
}

export default Navbar;
